<template>
  <v-dialog v-model="dialog" persistent max-width="720">
    <v-card v-if="Transaction" v-draggable-dialog class="detail-dialog">
      <div class="dialog-drag-handle detail-dialog__header">
        <div class="d-flex align-center min-width-0">
          <v-avatar size="42" color="green lighten-5" class="mr-3 flex-shrink-0">
            <v-icon color="primary">mdi-code-json</v-icon>
          </v-avatar>
          <div class="min-width-0">
            <div class="text-h6 font-weight-bold detail-title">Transaction details</div>
            <div class="text-body-2 text--secondary text-truncate">
              {{ Transaction.transactionId || `Transaction #${Transaction.id}` }}
            </div>
          </div>
        </div>
        <div class="d-flex align-center flex-shrink-0">
          <v-icon small color="grey lighten-1" class="mr-2 drag-indicator">mdi-drag</v-icon>
          <v-btn icon aria-label="Close transaction details" @click.stop="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <v-divider />

      <div class="detail-toolbar">
        <div class="breadcrumb-wrap">
          <button
            v-for="(title, index) in toolbarTitle"
            :key="`${title}-${index}`"
            type="button"
            class="breadcrumb-item"
            :class="{ 'breadcrumb-item--current': index === count }"
            @click="goToLevel(index)"
          >
            <v-icon v-if="index" x-small color="grey" class="mr-1">mdi-chevron-right</v-icon>
            <span>{{ displayKey(title) }}</span>
          </button>
        </div>

        <v-btn small outlined color="primary" :loading="syncing" @click="getTransaction">
          <v-icon left small>mdi-sync</v-icon>
          Re-sync status
        </v-btn>
      </div>

      <v-divider />

      <v-card-text class="pa-0">
        <div v-if="count > 0" class="px-5 pt-4">
          <v-btn text small color="primary" class="px-1" @click="goBack">
            <v-icon left small>mdi-arrow-left</v-icon>
            Back to {{ displayKey(toolbarTitle[count - 1]) }}
          </v-btn>
        </div>

        <div class="px-5 pt-4 pb-2">
          <div class="subtitle-1 font-weight-bold detail-title">{{ displayKey(toolbarTitle[count]) }}</div>
          <div class="caption text--secondary">
            {{ currentEntries.length }} {{ currentEntries.length === 1 ? "field" : "fields" }} at this level
          </div>
        </div>

        <div v-if="currentEntries.length" class="detail-list px-5 pb-5">
          <button
            v-for="entry in currentEntries"
            :key="entry.key"
            type="button"
            class="detail-row"
            :class="{ 'detail-row--expandable': isExpandable(entry.value) }"
            @click="isExpandable(entry.value) && showObject(entry.value, entry.key)"
          >
            <span class="detail-key">{{ displayKey(entry.key) }}</span>
            <span v-if="isExpandable(entry.value)" class="detail-nested">
              <v-chip x-small label color="green lighten-5" text-color="green darken-2">
                {{ nestedSummary(entry.value) }}
              </v-chip>
              <v-icon small color="primary">mdi-chevron-right</v-icon>
            </span>
            <span v-else class="detail-value" :title="formatValue(entry.value)">
              {{ formatValue(entry.value) }}
            </span>
          </button>
        </div>

        <div v-else class="empty-state px-6 py-10 text-center">
          <v-avatar size="48" color="grey lighten-4" class="mb-3">
            <v-icon color="grey">mdi-database-off-outline</v-icon>
          </v-avatar>
          <div class="font-weight-bold detail-title">No details at this level</div>
          <div class="text-body-2 text--secondary mt-1">There is no additional metadata to display.</div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import payCompassService from "@/modules/PayCompass/service.js";

export default {
  name: "open-transaction-detail",
  props: {
    openTransactionDetail: { default: false },
    selectedTransaction: { type: Object },
  },
  data() {
    return {
      dialog: false,
      syncing: false,
      Transaction: null,
      toolbarTitle: [],
      listData: [],
      count: 0,
    };
  },
  computed: {
    currentEntries() {
      const current = this.listData[this.count];
      if (!current || typeof current !== "object") return [];

      return Object.keys(current).map(key => ({ key, value: current[key] }));
    },
  },
  watch: {
    openTransactionDetail(val) {
      this.dialog = val;
      if (!val || !this.selectedTransaction) return;

      this.empty();
      this.Transaction = this.selectedTransaction;
      this.listData = [this.selectedTransaction.metadata || {}];
      this.toolbarTitle = ["Transaction metadata"];
    },
  },
  methods: {
    getTransaction() {
      this.syncing = true;
      return payCompassService.getTransactionById(this.Transaction.id)
        .then(() => {
          this.$swal.mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 2500,
          }).fire({ icon: "success", title: "Transaction status re-synced" });
        })
        .catch(error => {
          const message = error && error.data && error.data.message
            ? error.data.message
            : "Unable to re-sync the transaction status";
          this.$swal.mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          }).fire({ icon: "error", title: message });
        })
        .finally(() => {
          this.syncing = false;
        });
    },
    close() {
      this.dialog = false;
      this.empty();
      this.$emit("close");
    },
    displayKey(value) {
      return String(value || "Details")
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/^./, character => character.toUpperCase());
    },
    isExpandable(value) {
      return value !== null && typeof value === "object";
    },
    formatValue(value) {
      if (value === null || value === undefined || value === "") return "—";
      if (typeof value === "boolean") return value ? "Yes" : "No";
      return String(value);
    },
    nestedSummary(value) {
      if (Array.isArray(value)) return `${value.length} ${value.length === 1 ? "item" : "items"}`;
      const count = Object.keys(value).length;
      return `${count} ${count === 1 ? "field" : "fields"}`;
    },
    empty() {
      this.Transaction = null;
      this.listData = [];
      this.toolbarTitle = [];
      this.count = 0;
    },
    showObject(value, key) {
      this.listData.push(value);
      this.toolbarTitle.push(key);
      this.count += 1;
    },
    goBack() {
      if (this.count === 0) return;
      this.listData.pop();
      this.toolbarTitle.pop();
      this.count -= 1;
    },
    goToLevel(index) {
      if (index >= this.count) return;
      this.listData.splice(index + 1);
      this.toolbarTitle.splice(index + 1);
      this.count = index;
    },
  },
};
</script>

<style scoped>
.detail-dialog {
  border-radius: 14px !important;
  overflow: hidden;
}

.detail-dialog__header {
  min-height: 76px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: move;
  user-select: none;
}

.detail-title {
  color: #172033;
}

.min-width-0 {
  min-width: 0;
}

.detail-toolbar {
  min-height: 58px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #f7faf7;
}

.breadcrumb-wrap {
  min-width: 0;
  display: flex;
  align-items: center;
  overflow-x: auto;
}

.breadcrumb-item {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  padding: 4px 0;
  color: #657064;
  font-size: 0.78rem;
}

.breadcrumb-item--current {
  color: #2e7d32;
  font-weight: 700;
  pointer-events: none;
}

.detail-list {
  max-height: 52vh;
  overflow-y: auto;
}

.detail-row {
  width: 100%;
  min-height: 54px;
  padding: 10px 14px;
  display: grid;
  grid-template-columns: minmax(140px, 0.7fr) minmax(180px, 1.3fr);
  align-items: center;
  gap: 20px;
  border: 0;
  border-bottom: 1px solid #e8ece8;
  background: transparent;
  text-align: left;
}

.detail-row--expandable:hover {
  background: #f4f9f4;
}

.detail-key {
  color: #657064;
  font-size: 0.82rem;
  font-weight: 600;
}

.detail-value {
  color: #172033;
  overflow-wrap: anywhere;
}

.detail-nested {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

@media (max-width: 600px) {
  .detail-dialog__header {
    padding: 14px 16px;
  }

  .drag-indicator {
    display: none;
  }

  .detail-toolbar {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
    padding: 10px 16px;
  }

  .detail-toolbar .v-btn {
    align-self: flex-start;
  }

  .detail-row {
    grid-template-columns: 1fr;
    gap: 5px;
  }

  .detail-nested {
    justify-content: flex-start;
  }
}
</style>
